import { useEffect, useState } from 'react';

/**
 * App-wide font-family preference. Mirrors `useTextSize`: persists to
 * localStorage and drives a `data-app-font` attribute on <html>, which CSS
 * maps to the `--app-font` variable that Tailwind's `font-sans` resolves to
 * (see index.css + tailwind.config.js). So changing it re-fonts the whole app
 * instantly, with no React state threading.
 *
 * Geomanist is RxFood's brand face (self-hosted, the default); Inter loads
 * from Google Fonts (index.html); System uses the platform UI stack. Per-device
 * (not per-profile), like Theme + Text size.
 */

export type AppFont = 'geomanist' | 'inter' | 'system';

const STORAGE_KEY = 'ui.appFont';
const DEFAULT_FONT: AppFont = 'geomanist';

const isAppFont = (v: unknown): v is AppFont =>
  v === 'geomanist' || v === 'inter' || v === 'system';

const readStored = (): AppFont => {
  if (typeof window === 'undefined') return DEFAULT_FONT;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (isAppFont(raw)) return raw;
  } catch {
    /* private mode — fall through */
  }
  return DEFAULT_FONT;
};

const writeStored = (value: AppFont) => {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* ignore */
  }
};

const applyToDocument = (value: AppFont) => {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.appFont = value;
};

export const useAppFont = (): [AppFont, (next: AppFont) => void] => {
  const [font, setFont] = useState<AppFont>(() => readStored());

  // Apply on mount AND whenever it changes so the attribute stays in sync —
  // components sharing this hook coordinate through the single DOM write.
  useEffect(() => {
    applyToDocument(font);
    writeStored(font);
  }, [font]);

  return [font, setFont];
};

/** Apply the stored font as early as possible (before first paint of content). */
export const useApplyStoredAppFont = (): void => {
  useEffect(() => {
    applyToDocument(readStored());
  }, []);
};
